import React, {useCallback} from 'react';
import assert from 'assert-ts';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import type {BulkUpdateCreate} from 'services/bulkUpdates/types';
import {useLocalization} from 'localization';
import {
  bulkUpdateItemsCleared,
  selectBulkUpdateItemsItemIds,
  selectBulkUpdateItemsType,
} from 'services/bulkUpdateItems/reducer';
import {useCreateBulkUpdateWorkMutation} from 'services/bulkUpdates/bulkUpdatesApi';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {Scene, useSnacks} from 'components';
import {useSceneExtensionsContext} from 'sceneExtensions';
import {BulkUpdateTitle, NewBulkUpdateWizard} from './components';
import {useBulkUpdateTitle} from './hooks';

export const BulkUpdateNewSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const bulkUpdateItemsType = useAppSelector(selectBulkUpdateItemsType);
  const bulkUpdateItemsItemIds = useAppSelector(selectBulkUpdateItemsItemIds);

  const title = useBulkUpdateTitle(
    bulkUpdateItemsType,
    bulkUpdateItemsItemIds.length,
  );
  const {currentExtension} = useSceneExtensionsContext();

  const [createBulkUpdate] = useCreateBulkUpdateWorkMutation();
  const {successSnack, errorSnack} = useSnacks();

  const handleCancel = useCallback(() => {
    navigate('/bulkUpdate');
  }, [navigate]);

  const handleSubmit = useCallback(
    (bulkUpdate: Partial<BulkUpdateCreate>, ids: string[]) => {
      assert(
        bulkUpdate.type === Concept.work,
        'BulkUpdateNewSceneCore: Only work bulk updates are currently supported',
        {bulkUpdate, ids},
      );

      return createBulkUpdate({bulkUpdate: bulkUpdate as BulkUpdateCreate, ids})
        .unwrap()
        .then(bulkUpdateCreateResponse => {
          successSnack(t('page.bulkUpdate.new.submit.success'));
          // Allow snacks to show before navigating
          setTimeout(() => {
            navigate(`/bulkUpdate/details/${bulkUpdateCreateResponse.id}`);
            // Clear submitted items from state.
            dispatch(bulkUpdateItemsCleared());
          }, 500);
        })
        .catch(error => {
          errorSnack(t('page.bulkUpdate.new.submit.failed', error));
        });
    },
    [createBulkUpdate, dispatch, errorSnack, navigate, successSnack, t],
  );

  return (
    <Scene
      browserTitle={t('appbar.bulkUpdate')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Content loadStatus={'Loaded'}>
        <BulkUpdateTitle title={title} type={bulkUpdateItemsType} />

        <NewBulkUpdateWizard
          bulkUpdateType={bulkUpdateItemsType}
          itemIds={bulkUpdateItemsItemIds}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
